import React from 'react';
import {Modal, Text, Button, Icon, StatusBadge} from '@/ui-kits';
import {PatientAccountTicket} from '@/interface';
import {PatientAccountTicketRequestStatus, PatientAccountTicketType} from '@/constants';
import {PatientAccountRequestRequestTypeLabels, PatientAccountTicketRequestStatusBadgeVariants} from '@/constants/defaults';
import {formatDate} from '@/utils/common_functions/dateUtils';
import { patientAccountTicketLoadingSelector } from '@/services/redux/selectors/patientAdminSelector';
import { useSelector } from 'react-redux';

interface PausedRequestDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: PatientAccountTicket | null;
  onRemoveHold?: () => void;
  onApproveDeletion?: () => void;
}

export const PausedRequestDetailsModal: React.FC<PausedRequestDetailsModalProps> = ({
  isOpen,
  onClose,
  ticket,
  onRemoveHold,
  onApproveDeletion,
}) => {
  if (!isOpen || !ticket) return null;
  const isLoading = useSelector(patientAccountTicketLoadingSelector);
  // Blockers list - this could come from the API in the future
  const blockers = ['Ongoing Consultation(s)', 'Pending Refund(s)'];

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      className="rounded-2xl p-0 w-[95vw] sm:w-[90vw] md:w-[80vw] lg:w-[600px] max-w-[600px] bg-white shadow-2xl">
      <div className="flex flex-col bg-white rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 px-6 pt-6 pb-2">
          <div className="flex items-center justify-between">
            <Text variant="heading" className="text-ui_blue font-FigtreeBold">
              Request Details
            </Text>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-gray-100 transition flex-shrink-0">
              <Icon type="close" className="size-6 text-ui_blue" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 px-6 pb-6 pt-2 flex flex-col gap-6">
          {/* Content with background */}
          <div className="bg-profile_feedback_bg rounded-xl border border-ui_blue/20 p-3 flex flex-col gap-3">
            {/* Patient Info Section */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Icon type="profile_logo" className="size-7 text-ui_input_border_dark" />
                <Text variant="body" className="!text-deep_blue !font-FigtreeBold !text-xl">
                  Patient Info
                </Text>
              </div>
              <div className="flex flex-col gap-2 pl-9 pt-1">
                <Text variant="body" className="!text-deep_blue !font-FigtreeRegular">
                  Registered on:{' '}
                  <span className="!text-deep_blue !font-FigtreeSemiBold">
                    {formatDate(ticket.patient.created_at, 'dd-MMM-yyyy')}
                  </span>
                </Text>
                <Text variant="body" className="!text-deep_blue !font-FigtreeRegular">
                  Last Activity:{' '}
                  <span className="!text-deep_blue !font-FigtreeSemiBold">
                    {formatDate(ticket.patient.last_activity, 'dd-MMM-yyyy')}
                  </span>
                </Text>
              </div>
            </div>

            {/* Deletion & Export Summary Section */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Icon type="request_summary" className="size-6 text-ui_input_border_dark" />
                <Text variant="body" className="!text-deep_blue !font-FigtreeBold !text-xl">
                  Deletion & Export Summary
                </Text>
              </div>
              <div className="flex flex-col gap-2 pl-9 pt-1">
                <Text variant="body" className="!text-deep_blue !font-FigtreeRegular">
                  Type:{' '}
                  <span className="!text-deep_blue !font-FigtreeSemiBold">{PatientAccountRequestRequestTypeLabels[ticket.type.id as PatientAccountTicketType]}</span>
                </Text>
                <Text variant="body" className="!text-deep_blue !font-FigtreeSemiBold mt-4">
                  Deletion Status:{' '}
                  <span className="!font-FigtreeRegular !text-deep_blue">
                    Paused (Blockers detected)
                  </span>
                </Text>
                <div className="flex items-center gap-2">
                  <Text variant="body" className="!text-deep_blue !font-FigtreeSemiBold">
                    Current Status:
                  </Text>
                  <StatusBadge variant="secondary" status={PatientAccountTicketRequestStatusBadgeVariants[ticket.status.id as PatientAccountTicketRequestStatus]}>
                    {ticket.status.label}
                  </StatusBadge>
                </div>
                <Text variant="body" className="!text-deep_blue !font-FigtreeSemiBold">
                  Hold Status:{' '}
                  <span className="!text-deep_blue !font-FigtreeRegular">
                    Operational Hold - Pending Issues
                  </span>
                </Text>
              </div>
            </div>
          </div>

          {/* Blockers Section */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Icon type="close" className="size-5 text-red-600" />
              <Text variant="body" className="!text-deep_blue !font-FigtreeBold !text-lg">
                Blockers
              </Text>
            </div>
            <div className="flex flex-col gap-2 pl-7">
              {blockers.map((blocker, index) => (
                <Text
                  key={index}
                  variant="body"
                  className="!text-deep_blue !font-FigtreeRegular">
                  • {blocker}
                </Text>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

